import React, { useState, useEffect, useCallback } from 'react';
import texasApi from '../../services/texasApi';
import { getAppMarket } from '../../services/MarketService';
import { HttpErrorKind, classifyHttpError } from '../../services/httpError';

export interface IntegrationAdapter {
  id: string;
  name: string;
  jurisdiction: string;
  enabled: boolean;
  status: 'HEALTHY' | 'DEGRADED' | 'OFFLINE';
  lastCheck?: string;
  cachedFallbackAvailable?: boolean;
}

export interface CCPASnapshot {
  tenantId: string;
  checkedAt: string;
  overallSeverity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  compliant: boolean;
  residency: {
    compliant: boolean;
    region: string;
    reason: string;
  };
  stripe: {
    compliant: boolean;
    issues: string[];
  };
  violations: Array<{
    code: string;
    severity: string;
    description: string;
    statuteRef?: string;
    penaltyPerViolation?: number;
  }>;
}

export interface DSRRequest {
  requestId: string;
  consumerEmail: string;
  requestType: 'RIGHT_TO_KNOW' | 'RIGHT_TO_DELETE';
  verificationStatus: 'PENDING_VERIFICATION' | 'VERIFIED' | 'REJECTED';
  rejectionReason?: string;
  submittedAtMs: number;
  deadlineMs: number;
  deadlineIso: string;
  daysRemaining: number;
  isExtended: boolean;
  isOverdue: boolean;
  status: 'RECEIVED' | 'VERIFIED' | 'FULFILLED' | 'REJECTED';
  fulfillmentData?: Record<string, unknown>;
  exemptionsApplied?: string[];
}

export const TexasComplianceConsole: React.FC = () => {
  const isTexas = getAppMarket() === 'texas';
  const [activeTab, setActiveTab] = useState<'integrations' | 'ccpa' | 'dsr'>('integrations');

  // Integrations state
  const [adapters, setAdapters] = useState<IntegrationAdapter[]>([]);
  const [selectedAdapter, setSelectedAdapter] = useState<string>('fema-nfhl');
  const [testPayload, setTestPayload] = useState<string>('{"address": "100 Main St, Dallas, TX"}');
  const [invokeResult, setInvokeResult] = useState<unknown | null>(null);

  // CCPA state
  const [ccpaSnapshot, setCcpaSnapshot] = useState<CCPASnapshot | null>(null);

  // DSR state
  const [dsrRequests, setDsrRequests] = useState<DSRRequest[]>([]);
  const [dsrSweepDate, setDsrSweepDate] = useState<string>('');
  const [dsrSweepResults, setDsrSweepResults] = useState<unknown | null>(null);
  const [newConsumerEmail, setNewConsumerEmail] = useState<string>('');
  const [newRequestType, setNewRequestType] = useState<'RIGHT_TO_KNOW' | 'RIGHT_TO_DELETE'>('RIGHT_TO_KNOW');
  const [auditTrail, setAuditTrail] = useState<unknown[] | null>(null);
  const [selectedDsrId, setSelectedDsrId] = useState<string | null>(null);

  // Common UI states
  const [loading, setLoading] = useState<boolean>(false);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // UX-11: the classified kind, so a 422 (validation) renders as an amber
  // request notice rather than a red fault — and is never a compliance
  // violation. Every catch routes through reportError().
  const [errorKind, setErrorKind] = useState<HttpErrorKind | null>(null);

  const reportError = useCallback((err: any, fallback: string) => {
    const c = classifyHttpError(err);
    setErrorStatus(typeof c.status === 'number' ? c.status : null);
    setErrorMessage(c.message || fallback);
    setErrorKind(c.kind);
  }, []);

  // Load Integrations
  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    setErrorStatus(null);
    try {
      const res = await texasApi.integrations.getAdapters();
      // Envelope handling: supports both { success, data } and direct { data: [...] }
      const d = res?.data;
      const list = Array.isArray(d) ? d : (d?.adapters || d?.data || [
        { id: 'fema-nfhl', name: 'FEMA NFHL Flood Zone Adapter', jurisdiction: 'US-TX', enabled: true, status: 'HEALTHY' },
        { id: 'trec-db', name: 'TREC Public Licence Registry', jurisdiction: 'US-TX', enabled: true, status: 'HEALTHY' },
        { id: 'county-records', name: 'Texas County Deed Records', jurisdiction: 'US-TX', enabled: true, status: 'HEALTHY' },
        { id: 'municipal-permits', name: 'Dallas/Houston Municipal Permit Registry', jurisdiction: 'US-TX', enabled: true, status: 'HEALTHY' },
        { id: 'court-scheduling', name: 'JP Court Docket Scheduler', jurisdiction: 'US-TX', enabled: true, status: 'DEGRADED' },
      ]);
      setAdapters(list);
    } catch (err: any) {
      reportError(err, 'Failed to load integration registry adapters');
    } finally {
      setLoading(false);
    }
  }, [reportError]);

  // Load CCPA Snapshot
  const fetchCCPA = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    setErrorStatus(null);
    try {
      const res = await texasApi.ccpa.getSnapshot();
      // Envelope handling: supports both { success, data } and direct data
      const d = res?.data;
      setCcpaSnapshot(Array.isArray(d) ? null : (d?.success ? d?.data : (d || null)));
    } catch (err: any) {
      reportError(err, 'Failed to load CCPA compliance snapshot');
    } finally {
      setLoading(false);
    }
  }, [reportError]);

  // Run CCPA Check
  const handleRunCCPACheck = async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await texasApi.ccpa.runCheck({
        awsRegion: 'us-east-2',
        hasCAResidents: true,
        stripeConfig: { country: 'US', currency: 'usd', webhookUrl: 'https://api.landlordpro.com/stripe/webhook', cardDataHosted: true }
      });
      setCcpaSnapshot(res?.data || null);
    } catch (err: any) {
      reportError(err, 'CCPA check failed');
    } finally {
      setLoading(false);
    }
  };

  // Load DSR Requests
  const fetchDSRRequests = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    setErrorStatus(null);
    try {
      const res = await texasApi.ccpaDsr.listRequests();
      // Envelope handling: supports both { success, data } and direct { data: [...] }
      const d = res?.data;
      setDsrRequests(Array.isArray(d) ? d : (d?.success ? (d?.data || []) : []));
    } catch (err: any) {
      reportError(err, 'Failed to list consumer DSR requests');
    } finally {
      setLoading(false);
    }
  }, [reportError]);

  // Initial load depending on active tab
  useEffect(() => {
    if (!isTexas) return;
    if (activeTab === 'integrations') fetchIntegrations();
    if (activeTab === 'ccpa') fetchCCPA();
    if (activeTab === 'dsr') fetchDSRRequests();
  }, [isTexas, activeTab, fetchIntegrations, fetchCCPA, fetchDSRRequests]);

  // Handle adapter test invoke
  const handleTestInvoke = async () => {
    setLoading(true);
    setInvokeResult(null);
    setErrorMessage(null);
    try {
      let parsedPayload = {};
      try {
        parsedPayload = JSON.parse(testPayload);
      } catch {
        parsedPayload = { query: testPayload };
      }
      const res = await texasApi.integrations.testInvoke(selectedAdapter, parsedPayload);
      setInvokeResult(res?.data || res);
    } catch (err: any) {
      reportError(err, 'Test invoke failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle Create DSR Request
  const handleCreateDSR = async () => {
    if (!newConsumerEmail) {
      setErrorMessage('Consumer email is required to submit a DSR');
      return;
    }
    setLoading(true);
    setErrorMessage(null);
    try {
      // Contract: POST /api/texas/ccpa/dsr/requests requires `category`
      // (RIGHT_TO_KNOW | RIGHT_TO_DELETE) and `consumerId`. Sending
      // `requestType` without either failed validation with a 400.
      const consumerEmail = newConsumerEmail.trim().toLowerCase();
      await texasApi.ccpaDsr.createRequest({
        category: newRequestType,
        consumerId: consumerEmail,
        consumerEmail,
      });
      setNewConsumerEmail('');
      fetchDSRRequests();
    } catch (err: any) {
      reportError(err, 'Failed to create DSR request');
    } finally {
      setLoading(false);
    }
  };

  // Handle Verify DSR
  const handleVerifyDSR = async (requestId: string, verified: boolean) => {
    setLoading(true);
    try {
      await texasApi.ccpaDsr.verifyRequest(requestId, { verified, method: 'GOV_ID_PORTAL' });
      fetchDSRRequests();
    } catch (err: any) {
      reportError(err, 'Verification update failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle Fulfill DSR
  const handleFulfillDSR = async (requestId: string) => {
    setLoading(true);
    try {
      await texasApi.ccpaDsr.fulfillRequest(requestId, { fulfilledBy: 'privacy-officer' });
      fetchDSRRequests();
    } catch (err: any) {
      reportError(err, 'DSR fulfillment failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle View Audit Trail
  const handleViewAudit = async (requestId: string) => {
    setSelectedDsrId(requestId);
    setLoading(true);
    try {
      const res = await texasApi.ccpaDsr.getAuditTrail(requestId);
      setAuditTrail(res?.data || []);
    } catch (err: any) {
      reportError(err, 'Failed to load DSR audit trail');
    } finally {
      setLoading(false);
    }
  };

  // Handle Deadline Sweep
  const handleRunSweep = async () => {
    setLoading(true);
    try {
      const res = await texasApi.ccpaDsr.runDeadlineSweep(dsrSweepDate || undefined);
      setDsrSweepResults(res?.data || res);
    } catch (err: any) {
      reportError(err, 'Deadline sweep failed');
    } finally {
      setLoading(false);
    }
  };

  // Market Gating Check (AC-2)
  if (!isTexas) {
    return (
      <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-amber-800" data-testid="texas-market-gate">
        <h3 className="font-semibold text-lg mb-2">Market Gated Feature</h3>
        <p>The Texas Compliance &amp; Privacy Console is available exclusively in the Texas market instance.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="texas-compliance-console">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between border-b pb-4 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Texas Compliance &amp; Privacy Console</h1>
          <p className="text-sm text-gray-600">Surfaces Integration Registry (F-271), CCPA Checker (F-078), and DSR Tools (F-080)</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('integrations')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${activeTab === 'integrations' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            data-testid="tab-integrations"
          >
            <i className="fas fa-plug mr-2"></i>Integrations (F-271)
          </button>
          <button
            onClick={() => setActiveTab('ccpa')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${activeTab === 'ccpa' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            data-testid="tab-ccpa"
          >
            <i className="fas fa-shield-alt mr-2"></i>CCPA Checker (F-078)
          </button>
          <button
            onClick={() => setActiveTab('dsr')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${activeTab === 'dsr' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            data-testid="tab-dsr"
          >
            <i className="fas fa-user-lock mr-2"></i>DSR Manager (F-080)
          </button>
        </div>
      </div>

      {/* UX-11: a validation response (HTTP 422/400) is a fact about the
          REQUEST, not a compliance finding. Amber, explicitly labelled, and
          never counted among violations. */}
      {errorMessage && errorKind === 'validation' && (
        <div className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded text-amber-900 flex justify-between items-center" data-testid="validation-callout" role="status">
          <div>
            <span className="font-bold">{errorStatus === 422 ? '422 Validation: ' : 'Request validation: '}</span>
            <span>{errorMessage}</span>
            <span className="block text-xs text-amber-700 mt-1">
              This is a request validation response, not a compliance violation.
            </span>
          </div>
          <button onClick={() => { setErrorMessage(null); setErrorStatus(null); setErrorKind(null); }} className="text-amber-700 hover:text-amber-900 text-sm font-semibold">
            Dismiss
          </button>
        </div>
      )}

      {/* Actionable Error State Callout (AC-3) — genuine faults only. */}
      {errorMessage && errorKind !== 'validation' && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded text-red-800 flex justify-between items-center" data-testid="error-callout">
          <div>
            <span className="font-bold">
              {errorStatus === 401 && '401 Unauthorized: '}
              {errorStatus === 403 && '403 Forbidden: '}
              {errorStatus === 404 && '404 Not Found: '}
              {errorStatus === 500 && '500 Server Error: '}
            </span>
            <span>{errorMessage}</span>
          </div>
          <button onClick={() => { setErrorMessage(null); setErrorStatus(null); setErrorKind(null); }} className="text-red-600 hover:text-red-900 text-sm font-semibold">
            Dismiss
          </button>
        </div>
      )}

      {/* TAB 1: INTEGRATION REGISTRY (F-271) */}
      {activeTab === 'integrations' && (
        <div className="space-y-6" data-testid="section-integrations">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Live Integration Registry Adapters</h2>
            {loading && adapters.length === 0 ? (
              <p className="text-gray-500 py-4">Loading integration adapters...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Adapter ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Jurisdiction</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {adapters.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-sm text-indigo-600">{item.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{item.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.jurisdiction}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${item.status === 'HEALTHY' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Test Invoke Live Provider Panel */}
          <div className="bg-white shadow rounded-lg p-6 space-y-4">
            <h3 className="text-md font-semibold text-gray-900">Test-Invoke Adapter (`POST /api/texas/integrations/:adapter/lookup`)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Adapter</label>
                <select
                  value={selectedAdapter}
                  onChange={(e) => setSelectedAdapter(e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm p-2 border"
                  data-testid="select-adapter"
                >
                  <option value="fema-nfhl">FEMA NFHL Flood Zone Adapter</option>
                  <option value="trec-db">TREC Public Licence Registry</option>
                  <option value="county-records">Texas County Deed Records</option>
                  <option value="municipal-permits">Dallas/Houston Permit Registry</option>
                  <option value="court-scheduling">JP Court Docket Scheduler</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payload JSON</label>
                <textarea
                  value={testPayload}
                  onChange={(e) => setTestPayload(e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm p-2 border font-mono text-sm h-24"
                  data-testid="input-payload"
                />
              </div>
            </div>
            <button
              onClick={handleTestInvoke}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
              data-testid="btn-test-invoke"
            >
              {loading ? 'Invoking...' : 'Execute Test Invoke'}
            </button>

            {invokeResult !== null && (
              <div className="mt-4 p-4 bg-gray-900 text-green-400 rounded-md font-mono text-sm overflow-x-auto" data-testid="invoke-result">
                <pre>{JSON.stringify(invokeResult, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: CCPA COMPLIANCE CHECKER (F-078) */}
      {activeTab === 'ccpa' && (
        <div className="space-y-6" data-testid="section-ccpa">
          <div className="bg-white shadow rounded-lg p-6 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">CCPA &amp; US Compliance Audit Snapshot</h2>
                <p className="text-sm text-gray-500">Evaluates US AWS data residency, Stripe payment compliance, and CCPA penalties</p>
              </div>
              <button
                onClick={handleRunCCPACheck}
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700"
                data-testid="btn-run-ccpa-check"
              >
                {loading ? 'Auditing...' : 'Run Compliance Audit'}
              </button>
            </div>

            {ccpaSnapshot ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                <div className="p-4 bg-gray-50 rounded border">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Overall Compliance Status</span>
                  <p className={`text-xl font-bold mt-1 ${ccpaSnapshot.compliant ? 'text-emerald-600' : 'text-red-600'}`}>
                    {ccpaSnapshot.compliant ? 'COMPLIANT' : 'NON-COMPLIANT'}
                  </p>
                  <span className="text-xs text-gray-500">Severity: {ccpaSnapshot.overallSeverity}</span>
                </div>
                <div className="p-4 bg-gray-50 rounded border">
                  <span className="text-xs font-semibold text-gray-500 uppercase">US AWS Data Residency</span>
                  <p className={`text-xl font-bold mt-1 ${ccpaSnapshot.residency?.compliant ? 'text-emerald-600' : 'text-red-600'}`}>
                    {ccpaSnapshot.residency?.compliant ? 'US BOUNDARY PASS' : 'RESIDENCY VIOLATION'}
                  </p>
                  <span className="text-xs text-gray-500">{ccpaSnapshot.residency?.reason}</span>
                </div>
                <div className="p-4 bg-gray-50 rounded border">
                  <span className="text-xs font-semibold text-gray-500 uppercase">US Stripe Payment Compliance</span>
                  <p className={`text-xl font-bold mt-1 ${ccpaSnapshot.stripe?.compliant ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {ccpaSnapshot.stripe?.compliant ? 'STRIPE PASSED' : 'STRIPE ISSUES'}
                  </p>
                  <span className="text-xs text-gray-500">{ccpaSnapshot.stripe?.issues?.length || 0} issues flagged</span>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500 bg-gray-50 rounded border" data-testid="no-snapshot-state">
                No active CCPA snapshot found. Click "Run Compliance Audit" to evaluate tenant data.
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: DATA SUBJECT REQUEST (DSR) MANAGER (F-080) */}
      {activeTab === 'dsr' && (
        <div className="space-y-6" data-testid="section-dsr">
          {/* Create Request Form */}
          <div className="bg-white shadow rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Submit Consumer Data Subject Request (DSR)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Consumer Email</label>
                <input
                  type="email"
                  placeholder="consumer@example.com"
                  value={newConsumerEmail}
                  onChange={(e) => setNewConsumerEmail(e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm p-2 border"
                  data-testid="input-dsr-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Request Type</label>
                <select
                  value={newRequestType}
                  onChange={(e) => setNewRequestType(e.target.value as any)}
                  className="w-full border-gray-300 rounded-md shadow-sm p-2 border"
                  data-testid="select-dsr-type"
                >
                  <option value="RIGHT_TO_KNOW">Right to Know (Disclose Data)</option>
                  <option value="RIGHT_TO_DELETE">Right to Delete (Erase Data)</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleCreateDSR}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700"
                  data-testid="btn-create-dsr"
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>

          {/* DSR Active Requests Table */}
          <div className="bg-white shadow rounded-lg p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-md font-semibold text-gray-900">Consumer DSR Requests &amp; 45-Day Response Clock</h3>
              <div className="flex space-x-2">
                <input
                  type="date"
                  value={dsrSweepDate}
                  onChange={(e) => setDsrSweepDate(e.target.value)}
                  className="border-gray-300 rounded-md p-1 border text-sm"
                  data-testid="input-sweep-date"
                />
                <button
                  onClick={handleRunSweep}
                  className="px-3 py-1 bg-gray-100 text-gray-700 font-medium text-sm rounded hover:bg-gray-200 border"
                  data-testid="btn-run-sweep"
                >
                  GSI1 Deadline Sweep
                </button>
              </div>
            </div>

            {dsrSweepResults !== null && (
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded font-mono text-xs overflow-x-auto" data-testid="sweep-results">
                <pre>{JSON.stringify(dsrSweepResults, null, 2)}</pre>
              </div>
            )}

            {dsrRequests.length === 0 ? (
              <p className="text-gray-500 py-4 text-center" data-testid="no-dsr-state">No DSR requests currently filed.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Request ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Verification</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Statutory Clock</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dsrRequests.map((req) => (
                      <tr key={req.requestId} className="hover:bg-gray-50" data-testid={`dsr-row-${req.requestId}`}>
                        <td className="px-4 py-3 font-mono text-sm text-indigo-600">{req.requestId}</td>
                        <td className="px-4 py-3 text-sm font-semibold">{req.requestType}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${req.verificationStatus === 'VERIFIED' ? 'bg-emerald-100 text-emerald-800' : req.verificationStatus === 'REJECTED' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                            {req.verificationStatus}
                          </span>
                        </td>
                        {/* AC-1: Renders backend statutory clock computation directly */}
                        <td className="px-4 py-3 text-sm" data-testid={`clock-${req.requestId}`}>
                          <div className={`font-bold ${req.isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                            {req.daysRemaining} days remaining
                          </div>
                          <div className="text-xs text-gray-500">Deadline: {req.deadlineIso}</div>
                        </td>
                        <td className="px-4 py-3 text-sm space-x-2">
                          {req.verificationStatus === 'PENDING_VERIFICATION' && (
                            <button
                              onClick={() => handleVerifyDSR(req.requestId, true)}
                              className="px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700"
                              data-testid={`btn-verify-${req.requestId}`}
                            >
                              Verify ID
                            </button>
                          )}
                          {req.verificationStatus === 'VERIFIED' && req.status !== 'FULFILLED' && (
                            <button
                              onClick={() => handleFulfillDSR(req.requestId)}
                              className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700"
                              data-testid={`btn-fulfill-${req.requestId}`}
                            >
                              Fulfill
                            </button>
                          )}
                          <button
                            onClick={() => handleViewAudit(req.requestId)}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 border"
                            data-testid={`btn-audit-${req.requestId}`}
                          >
                            Audit Trail
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Audit Trail Viewer */}
            {auditTrail !== null && (
              <div className="mt-4 p-4 bg-gray-50 border rounded space-y-2" data-testid="audit-trail-panel">
                <h4 className="font-semibold text-sm text-gray-900">Append-Only Audit Trail for {selectedDsrId}</h4>
                <div className="font-mono text-xs max-h-48 overflow-y-auto space-y-1">
                  {auditTrail.map((log: any, idx: number) => (
                    <div key={idx} className="p-2 bg-white border rounded">
                      <span className="text-gray-500">{log.timestamp || log.timestampMs}</span> — <span className="font-bold text-indigo-600">{log.action}</span>: {log.details}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
